/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/**
 * Encapsulates using the route to generate the corresponding href (and target)
 * and goto (and gotoTarget) for links and menus.
 * It is recommended that you use ConcreteRouteFactory to create a ConcreteRoute instance as it preforms
 * stronger type checking than a declarative instance (e.g., ensures navigationRoute and routeProps
 * members have same routePropsT type).
 * <p>You must use {@link concreteRouteFactory} to create an instance of this interface as the
 * factory supports stronger build-time checking and auto-complete than declarative instances.
 */

/**
 * Creates strongly-typed ConcreteRoute instance from a registeredRoute and
 * routeProps, including type checking that the
 * routeProps argument type is supported by RegisteredRoute.  The ConcreateRoute isAuthorized hook
 * is backed by the underlying RegisteredRoute.routeSpec.isAuthorized implementation (defaults to true)
 * @see ConcreteRoute.
 */
export function concreteRouteFactory(regRoute, routeProps,
// will get compiler error if default to {}!
optProps = {}) {
  const isAuthorized = regRoute.routeSpec.isAuthorized ? () => regRoute.routeSpec.isAuthorized(routeProps) : undefined; // defaults to ()=>true
  return concreteNavRouteFactory(regRoute, routeProps, {
    ...optProps,
    isAuthorized
  });
}
/**
 * Creates strongly-typed ConcreteRoute instance from lower-level NavigationRoute, and type checks that the
 * routeProps argument type is supported by navigationRoute.  Caller can optionally provide isAuthorized prop
 * to control authorization.
 * Primarily used for tests as NavigationRoute is easier to mock than a RegisterRoute.  Prefer to use
 * concreteRouteFactory if have the registeredRoute available.
 * @see ConcreteRoute.
 * @see concreteRouteFactory.
 */
export function concreteNavRouteFactory(navigationRoute, routeProps,
// will get compiler error if default to {}!
optProps = {}) {
  const goto = (currentRouteRenderContext, addCurrentRouteToBrowserHistory = true) => {
    const gotoTarget = optProps.gotoTarget ?? currentRouteRenderContext;
    return navigationRoute.goto(routeProps, {
      addCurrentRouteToBrowserHistory,
      routeRenderContext: gotoTarget
    });
  };
  // Do NOT precalculate/cache href as navigationRoute instance may depend on services that are not yet initialized (portal)
  const href = () => navigationRoute.href(routeProps);

  // Default isAuthorized to true if not provided.
  // As isAuthorized is synchronous, no rpc calls, so just 'map lookups' and should be fast - no need to cache
  const isAuthorized = optProps.isAuthorized ?? (() => true);
  return {
    // [CONCRETE_ROUTE]: true,
    goto,
    href,
    target: optProps.target,
    isAuthorized
  };
}

/**
 * Create concreteRoute instance for a 'no prop' route by directly providing href and gotoFn.
 * @param href The href for the navigation route.  It is typically a route href like "#/myRoute/33',
 * but can be full url as well.
 * @param gotoFn
 * @param optProps
 */
export function noPropsConcreteRouteFactory(href, gotoFn, optProps = {}) {
  const hrefFn = typeof href === "function" ? href : () => href;
  return concreteNavRouteFactory({
    goto: propsIgnored => gotoFn(),
    href: propsIgnored => hrefFn()
  }, {}, optProps);
}

/**
 * Create concreteRoute instance for a 'no prop' route that only has an href.
 * It will generate a goto function that internally has the same behavior as the href and target behavior.
 *
 * @param href function that return an href string, which should start with '#'.
 * Alternatively, can just provide the href string.
 * @param optProps Can provide target or isAuthorized props.
 * The target prop sets href target for link.  Defaults to undefined, which the anchor 'target' interprets as "_self".
 * Use "_blank" to open in new browser tab.
 * Can also open route in an existing browser tab by providing the browser tab name (e.g., "myHelpTab")
 * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#target
 *
 * The isAuthorized prop determines whether user is authorized to navigate to the route.
 * If not provided, assumes user is authorized.
 */
export function hrefConcreteRouteFactory(href, optProps = {}) {
  const hrefFn = typeof href === "function" ? href : () => href;
  const gotoFn = () => {
    // Not allowed to invoke navigationRoute.href until link invoked as might depend on services/routes
    // that aren't built yet (e.g., Portal).  So do runtime check that href is valid.
    // Default target to _self to be consistent with default <a> 'target' behavior.
    window.open(hrefFn(), optProps.target ?? "_self");
    return Promise.resolve(true);
  };
  return noPropsConcreteRouteFactory(hrefFn, gotoFn, {
    ...optProps,
    gotoTarget: undefined
  });
}
//# sourceMappingURL=ConcreteRoute.js.map
